import React from 'react';
import { useTranslation } from 'react-i18next';

import { MapItem, FreightItem } from '../types';
import { formatDate, formatLocation } from '../utils';
import styles from '../styles.module.scss';

interface FreightItemCardProps {
  item: MapItem;
  isSelected: boolean;
  templateId?: string;
  searchRoute: string;
  itemIdParam: string;
  onClick: () => void;
}

export const FreightItemCard: React.FC<FreightItemCardProps> = ({
  item,
  isSelected,
  templateId,
  searchRoute,
  itemIdParam,
  onClick,
}) => {
  const { t } = useTranslation();
  const freight = item as FreightItem;
  const loadingAddr = freight.loading_address;
  const unloadingAddr = freight.unloading_address;
  const cargoName = freight.designation || freight.cargo_designations_additional;
  const bodyType = [freight.bodytype, freight.bodytype_additional].filter(Boolean).join(', ');
  const hasAdr = freight.adr_captions || (freight.adr_items && freight.adr_items.length > 0);

  const handleOpenClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(`${searchRoute}?templateId=${templateId}&${itemIdParam}=${freight.id}`, '_blank');
  };

  return (
    <div
      className={`${styles.freightCard} ${isSelected ? styles.freightCardSelected : ''}`}
    >
      <div className={styles.freightCardHeader} onClick={onClick}>
        <div className={styles.freightCardRoute}>
          <span className={styles.freightCardFrom}>
            {formatLocation(loadingAddr)}
            {(freight.loading_from || freight.loading_to) && (
              <span className={styles.freightCardDates}>
                {freight.loading_from && formatDate(freight.loading_from)}
                {freight.loading_from && freight.loading_to && ' – '}
                {freight.loading_to && formatDate(freight.loading_to)}
              </span>
            )}
          </span>
          <span className={styles.freightCardArrow}>&rarr;</span>
          <span className={styles.freightCardTo}>
            {formatLocation(unloadingAddr)}
            {(freight.unloading_from || freight.unloading_to) && (
              <span className={styles.freightCardDates}>
                {freight.unloading_from && formatDate(freight.unloading_from)}
                {freight.unloading_from && freight.unloading_to && ' – '}
                {freight.unloading_to && formatDate(freight.unloading_to)}
              </span>
            )}
          </span>
        </div>

        {cargoName && (
          <div className={styles.freightCardTransport}>
            <span className={styles.freightCardLabel}>{t('freight.designation', 'Наименование')}:</span> {cargoName}
          </div>
        )}

        <div className={styles.freightCardInfo}>
          {freight.weight && <span><span className={styles.freightCardLabel}>{t('freight.weight', 'Вес')}:</span> {freight.weight} {t('common.tons', 'т')}</span>}
          {freight.volume && <span><span className={styles.freightCardLabel}>{t('freight.volume', 'Объём')}:</span> {freight.volume} {t('common.cubicMeters', 'м³')}</span>}
          {hasAdr && <span className={styles.adrBadge}>ADR</span>}
        </div>

        {bodyType && (
          <div className={styles.freightCardTransport}>
            <span className={styles.freightCardLabel}>{t('freight.bodyType', 'Тип кузова')}:</span> {bodyType}
          </div>
        )}

        <div className={styles.freightCardMeta}>
          <span className={styles.freightCardLabel}>{t('freight.price', 'Цена')}:</span>{' '}
          <span className={styles.freightCardPrice}>
            {freight.price && Number(freight.price) !== 0 ? `${freight.price} ${freight.currency || ''}` : t('common.priceOnRequest', 'Цена по запросу')}
          </span>
        </div>

        {freight.notes && (
          <div className={styles.freightCardNotes}>
            <span className={styles.freightCardLabel}>{t('freight.notes', 'Примечание')}:</span> {freight.notes}
          </div>
        )}
      </div>
      <button
        className={styles.freightCardButton}
        onClick={handleOpenClick}
      >
        {t('common.open', 'Открыть')}
      </button>
    </div>
  );
};
